import { ref, onScopeDispose, getCurrentScope } from 'vue'

import { useMediaCapture } from './useMediaCapture.js'
import { pickRecordingType, extensionFor } from '../utils/audio.js'

/**
 * Un turno de conversación: escuchar hasta que la persona deja de hablar.
 *
 * ── Qué añade sobre `UiAudioRecorder` ────────────────────────────────────────
 *
 * El botón de grabar necesita DOS gestos: empezar y parar. Eso está bien para
 * dictar una nota —se piensa, se habla, se para— y no sirve para una
 * conversación: nadie sostiene un teléfono y va tocando «parar» después de cada
 * frase, y menos con guantes puestos y subido a una escalera. Aquí el que
 * decide que el turno ha terminado es el SILENCIO, igual que en una llamada.
 *
 * Así que esto no sustituye al grabador y no debe: son las dos maneras, y una
 * pantalla seria ofrece las dos.
 *
 * ── Cómo sabe que has terminado ──────────────────────────────────────────────
 *
 * Midiendo el volumen (Web Audio, `AnalyserNode`) y nada más. Ni modelo, ni
 * servicio, ni un byte que salga del dispositivo: es una media cuadrática sobre
 * la onda, que es justo lo que hace falta para distinguir «está hablando» de
 * «hay una furgoneta al fondo». Se calibra al empezar con el ruido de la propia
 * obra —un taller no suena como un despacho— y el umbral es ese suelo más un
 * margen.
 *
 * Tres finales, y los tres son respuestas normales:
 *
 *   · habló y se calló   → el audio, que es lo que se esperaba;
 *   · no dijo nada nunca → `null`, y quien llama pregunta otra vez. No es un
 *     fallo: es que no había nadie delante, o no se oyó la pregunta;
 *   · se enrolló         → el techo de segundos corta y devuelve lo grabado.
 *     Cortar es mejor que perderlo: lo dicho hasta ahí ya vale.
 *
 * ── Y por qué no escucha mientras el asistente habla ─────────────────────────
 *
 * Porque el micrófono oiría al altavoz. Interrumpir a media pregunta —lo que en
 * telefonía se llama «barge-in»— pide cancelación de eco, y la del navegador no
 * alcanza cuando la voz sale por el altavoz de un móvil en una obra. Quien
 * llama habla primero y escucha después; a cambio, se puede cortar la pregunta
 * con un botón.
 */

/** Cuánto silencio da por terminado el turno. Menos corta a quien piensa. */
const SILENCE_MS = 1500
/** Cuánto se espera a que alguien empiece a hablar antes de rendirse. */
const PATIENCE_MS = 7000
/** El techo. Una respuesta de un minuto ya no es una respuesta. */
const MAX_SECONDS = 60
/** Lo que se mide como «ruido de fondo» antes de dar la palabra. */
const CALIBRATION_MS = 350
/** Cuánto tiene que subir sobre el ruido de fondo para contar como voz. */
const MARGIN = 0.035
/** Suelo absoluto: en una habitación muda, el ruido de fondo es casi cero. */
const FLOOR = 0.02
/**
 * Lo más alto que se acepta como «ruido de fondo», por ruidoso que parezca.
 *
 * Sin este techo, quien contesta ANTES de que acabe la calibración se queda sin
 * ser oído en todo el turno: su propia voz entra como suelo de la sala, el
 * umbral sube por encima de ella y a partir de ahí nada la supera. Pasa justo
 * con quien ya sabe la pregunta y contesta encima — que son los rápidos, no los
 * despistados.
 */
const NOISE_CEILING = 0.12

export function useVoiceTurn() {
  const microphone = useMediaCapture()

  /** 'idle' · 'waiting' (aún no ha hablado) · 'hearing' (está hablando). */
  const phase = ref('idle')
  /** 0..1, para pintar que se le oye. Una barra quieta es un micro muerto. */
  const level = ref(0)

  const supported = Boolean(
    microphone.available
    && typeof window !== 'undefined'
    && typeof window.MediaRecorder === 'function'
    && (window.AudioContext || window.webkitAudioContext)
  )

  let stream = null
  let recorder = null
  let audio = null
  let frame = null
  let finish = null

  function release() {
    if (frame) cancelAnimationFrame(frame)
    frame = null
    if (audio) audio.close().catch(() => { /* ya estaba cerrado */ })
    audio = null
    microphone.release(stream)
    stream = null
    recorder = null
    phase.value = 'idle'
    level.value = 0
  }

  /**
   * Un turno.
   *
   * @param {object} [options]
   * @param {number} [options.silenceMs]
   * @param {number} [options.patienceMs]
   * @param {number} [options.maxSeconds]
   * @param {string} [options.filename]
   * @returns {Promise<File|null>} lo grabado, o `null` si no dijo nada
   * @throws {MediaCaptureError} si el micrófono no se puede ni pedir
   */
  async function listen({
    silenceMs = SILENCE_MS,
    patienceMs = PATIENCE_MS,
    maxSeconds = MAX_SECONDS,
    filename = 'turno',
  } = {}) {
    cancel()

    // Se deja subir el error: negado, sin micro, ocupado o página insegura son
    // cuatro respuestas distintas, y quien llama tiene que poder decir cuál.
    stream = await microphone.capture({ audio: true })

    const Context = window.AudioContext || window.webkitAudioContext
    audio = new Context()
    const analyser = audio.createAnalyser()
    analyser.fftSize = 1024
    audio.createMediaStreamSource(stream).connect(analyser)
    const wave = new Float32Array(analyser.fftSize)

    const picked = pickRecordingType(window.MediaRecorder.isTypeSupported)
    const chunks = []
    recorder = picked
      ? new window.MediaRecorder(stream, { mimeType: picked.mimeType })
      : new window.MediaRecorder(stream)
    recorder.addEventListener('dataavailable', event => {
      if (event.data?.size) chunks.push(event.data)
    })

    return new Promise((resolve, reject) => {
      let spoke = false
      let quietSince = 0
      // El MÍNIMO de los primeros milisegundos y no la media: la media de
      // alguien que ya está contestando es su voz.
      let noise = Infinity
      const started = Date.now()

      recorder.addEventListener('stop', () => {
        const type = recorder?.mimeType || picked?.mimeType || 'audio/webm'
        const blob = new Blob(chunks, { type })
        release()
        // Sin voz no se manda nada: transcribir un silencio cuesta lo mismo que
        // transcribir una frase y contesta lo que ya sabíamos.
        resolve(spoke && blob.size
          ? new File([blob], `${filename}.${extensionFor(type)}`, { type: blob.type })
          : null)
      })

      finish = () => {
        if (recorder && recorder.state !== 'inactive') recorder.stop()
        else { release(); resolve(null) }
      }

      const tick = () => {
        if (!audio) return
        analyser.getFloatTimeDomainData(wave)

        let sum = 0
        for (let i = 0; i < wave.length; i += 1) sum += wave[i] * wave[i]
        const loudness = Math.sqrt(sum / wave.length)
        level.value = Math.min(1, loudness * 8)

        const now = Date.now()
        const age = now - started

        // Primero se escucha la sala, y solo después se da por empezado el
        // turno: el umbral de una obra no es el de un despacho.
        if (age < CALIBRATION_MS) {
          noise = Math.min(noise, loudness)
          frame = requestAnimationFrame(tick)
          return
        }

        const floor = Math.min(Number.isFinite(noise) ? noise : 0, NOISE_CEILING)
        const threshold = Math.max(FLOOR, floor + MARGIN)

        if (loudness > threshold) {
          spoke = true
          quietSince = 0
          phase.value = 'hearing'
        } else if (spoke) {
          if (!quietSince) quietSince = now
          if (now - quietSince >= silenceMs) { finish(); return }
        } else if (age >= patienceMs) {
          // Nadie ha dicho nada. No es un fallo: se vuelve a preguntar.
          finish()
          return
        }

        if (age >= maxSeconds * 1000) { finish(); return }
        frame = requestAnimationFrame(tick)
      }

      try {
        recorder.start()
        phase.value = 'waiting'
        frame = requestAnimationFrame(tick)
      } catch (error) {
        release()
        reject(error)
      }
    })
  }

  /** Colgar a media escucha. Devuelve lo que hubiera, como el silencio. */
  function cancel() {
    if (finish) { const stop = finish; finish = null; stop() }
    else release()
  }

  if (getCurrentScope()) onScopeDispose(cancel)

  return { supported, phase, level, listen, cancel, problemLabel: microphone.problemLabel }
}

export { SILENCE_MS, PATIENCE_MS, MAX_SECONDS }
